/**
 * 利用者起点シェルコマンドの実行記録カード（管理アプリ用）
 *
 * プロンプト先頭の `!` で利用者自身が実行したコマンドとその結果を表示する。
 * AI のツール実行（ToolCallCard）とは別のカードで描き、監査時に「誰が実行したのか」を
 * 取り違えないようにする。デスクトップの ShellCommandDisplay と同じ項目を出す。
 */

import { TerminalIcon } from 'lucide-react';
import type { ShellCommandPart } from './task-detail-types';

/** 出力の 1 節。空のときは節ごと出さない（呼び出し側が「出力なし」を出す） */
function OutputSection({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all rounded bg-muted border border-border px-2 py-1 font-mono text-xs text-foreground">
        {value}
      </pre>
    </div>
  );
}

/** 実行状態に応じた結果部分 */
function ShellCommandResultBody({ part }: { part: ShellCommandPart }) {
  switch (part.status) {
    case 'running':
      // 保存済みの記録を読むビューでは進行中に出会わない（読み出し時に unknown へ正規化
      // される）。形式が想定外でも欠落させないよう、状態だけは出す
      return <div className="text-xs text-muted-foreground">実行中</div>;

    case 'unknown':
      return (
        <div className="text-xs text-muted-foreground">
          結果不明（実行の完了を確認できないまま記録が保存されました）
        </div>
      );

    case 'opaque':
      return (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">
            出力が大きいため切り詰められました（終了コードは取得できていません）
          </div>
          <OutputSection label="result" value={part.text ?? ''} />
        </div>
      );

    case 'errored':
      return (
        <div className="space-y-1">
          <div className="text-xs text-red-400">コマンドを実行できませんでした</div>
          <OutputSection label="error" value={part.errorMessage ?? ''} />
        </div>
      );

    case 'completed': {
      const stdout = part.stdout ?? '';
      const stderr = part.stderr ?? '';
      const hasOutput = stdout.length > 0 || stderr.length > 0;
      return (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className={part.exitCode === 0 ? 'text-muted-foreground' : 'text-red-400'}>
              終了コード {part.exitCode}
            </span>
            {part.executionTimeMs !== undefined && (
              <span className="text-muted-foreground">{part.executionTimeMs}ms</span>
            )}
            {part.timedOut && (
              <span className="text-red-400">実行時間の上限で打ち切られました</span>
            )}
          </div>
          {stdout.length > 0 && <OutputSection label="stdout" value={stdout} />}
          {stderr.length > 0 && <OutputSection label="stderr" value={stderr} />}
          {!hasOutput && <div className="text-xs text-muted-foreground">出力はありません</div>}
        </div>
      );
    }

    default:
      // 未知の状態でもカード全体を落とさない（コマンド本文だけは残す）
      return null;
  }
}

export function ShellCommandCard({ part }: { part: ShellCommandPart }) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
      <div className="flex items-start gap-2 font-mono text-sm text-foreground">
        <TerminalIcon className="mt-0.5 w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        <span className="shrink-0 select-none text-muted-foreground">!</span>
        <span className="whitespace-pre-wrap break-all">{part.command}</span>
      </div>
      <ShellCommandResultBody part={part} />
    </div>
  );
}
